import React from "react";

import { Text } from "../Text";
import {
	IconDefault,
	IconError,
	Illustration1,
	Illustration2,
	Illustration3,
	Illustration4,
	Illustration5,
	Illustration6,
	Illustration7,
	Illustration8,
	Illustration9,
} from "./assets";

/**
 * Icon option for EmptyState when variant is "icon"
 */
export type EmptyStateIconOption = "default" | "error";

/**
 * Illustration option for EmptyState when variant is "illustration"
 * Valid values are 1 through 9, each representing a different illustration design
 */
export type EmptyStateIllustrationOption = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

export type EmptyStateVariant = "icon" | "illustration";

export type EmptyStateProps = {
	/**
	 * Visual variant for EmptyState component
	 */
	variant: EmptyStateVariant;
	/**
	 * Label for EmptyState component
	 */
	label: string;
} & (
	| {
			variant: "icon";
			option: EmptyStateIconOption;
	  }
	| {
			variant: "illustration";
			option: EmptyStateIllustrationOption;
	  }
);

const getAsset = (variant: EmptyStateProps["variant"], option: string | number) => {
	if (variant === "icon") {
		switch (option) {
			case "default":
				return <IconDefault />;
			case "error":
				return <IconError />;
			default:
				return <IconDefault />;
		}
	}

	switch (option) {
		case 1:
			return <Illustration1 />;
		case 2:
			return <Illustration2 />;
		case 3:
			return <Illustration3 />;
		case 4:
			return <Illustration4 />;
		case 5:
			return <Illustration5 />;
		case 6:
			return <Illustration6 />;
		case 7:
			return <Illustration7 />;
		case 8:
			return <Illustration8 />;
		case 9:
			return <Illustration9 />;
		default:
			return <Illustration1 />;
	}
};

/**
 * EmptyState component displays a placeholder visual and message when there is no content to show.
 *
 * The component is centered both horizontally and vertically within its parent container.
 * It supports two visual variants: icons (for simple states) and illustrations (for more detailed empty states).
 *
 * @example
 * ```tsx
 * // Icon variant with default icon
 * <EmptyState variant="icon" option="default" label="No items to display" />
 *
 * // Icon variant with error icon
 * <EmptyState variant="icon" option="error" label="Something went wrong" />
 *
 * // Illustration variant
 * <EmptyState variant="illustration" option={1} label="No documents found" />
 * ```
 *
 * @param {EmptyStateProps} props - The component props
 * @returns {JSX.Element} The rendered EmptyState component
 */
export const EmptyState = ({ variant, option, label }: EmptyStateProps) => {
	const marginBottom = variant === "icon" ? "mb-2" : "mb-4";

	return (
		<div className="flex size-full items-center justify-center">
			<div className="flex flex-col items-center text-center">
				<div className={marginBottom}>{getAsset(variant, option)}</div>
				<Text size="paragraph-2" weight="semi-bold">
					{label}
				</Text>
			</div>
		</div>
	);
};
